import { HEX, SENDA, sec } from '@somnhold/content';
import { cellAtWorld, cellCenter, cellId } from '../grid.ts';
import { hexLen, hexToWorld } from '../hex.ts';
import { iatan2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { sendaCue } from './senda.ts';
import type { BattleState, Enemy, GuardRules, Hero, SaltRules, SaltState, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';
import { SALT_CAP, SALT_HALF_X, SALT_HALF_Y, saltPoolAt, sluiceFlow, type SaltLayout } from './salt-layout.ts';

/**
 * The salt flats (docs/sendas/paladin.md, chapter 2; §4, «Las salinas» and «La sal de Marit»). In the fog the
 * stalkers are unseen but on salt: the dykes between the pools, a pool gone dry (its crust), a circle of Marit's salt.
 * What walks the water or the altar's fresh mud can't be seen nor struck (`saltSeen`, read by combat's `isRevealed`).
 *
 * - The fog (act 1): Ser Marit follows the hero to the gate house, `follow` behind at her pace; she strikes what she
 *   sees. The stalkers come for her from the fog. Won with her at its door.
 * - The gates (act 2, the puzzle): a gate tapped, the hero goes and turns it, still `turn` by it; its water runs
 *   `flow`, then the marks move (battle/salt-layout.ts `sluiceFlow`) and it locks. Each `back` the sea comes in under
 *   the dykes: the pools as they were, the gates free; with the altar under water it counts, and `lose` of them lose it.
 *   Drowned climb out of a full pool by the hero, more with each return. After `nuno` gates Nuño comes out of the
 *   gate house to help (Ser Varo's leave or not). Won with the altar's pool dry.
 * - The test (act 3): the altar held while it lights (the fight's clock). Marit throws salt where she is told (one
 *   every `every`); it lasts `last` on salt and `wet` on water or mud, where it melts. What is in a circle is seen,
 *   slowed and salted a while. The stalkers come along two of four ways (one swapped at `swap`); their tracks show on
 *   the ways' dry pools and dykes far out. The old stalker leaps at Marit (its spot marked `warn` before); a hero on the
 *   spot takes it.
 *
 * Cost: a cell's pool is one look-up; the circles are three at most; the stalkers on a way are kept in a short sorted
 * list. Nothing walks every nightmare against another (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** The flats' rules from its layout (the fight's builder, campaign/sendas.ts; and the renderer's scenes). */
export const saltRules = (L: SaltLayout, radius: number, kind: SaltRules['kind'], marit: GuardRules, seed: number, oldHp: number, drownedHp: number): SaltRules => {
  const cells = (2 * radius + 1) * (2 * radius + 1);
  const cell: number[] = new Array<number>(cells).fill(-2);
  const W = 2 * radius + 1;
  const houseCells = new Set<number>();
  for (const h of L.rocks) {
    const id = cellId(radius, h.q, h.r);
    if (id >= 0) houseCells.add(id);
  }
  for (let r = -radius; r <= radius; r++)
    for (let q = -radius; q <= radius; q++) {
      if (hexLen(q, r) > radius) continue;
      const id = (r + radius) * W + (q + radius);
      if (houseCells.has(id)) continue;
      const w = hexToWorld(q, r);
      cell[id] = saltPoolAt(L.pools, w.x, w.y);
    }
  const S = SENDA.salt;
  const A = L.puzzle.A;
  const a = L.pools[A] as { x: number; y: number };
  const start = L.start.slice();
  // The test: the altar's pool drained (its mud), the ways' outer pools dry; the rest as the gates left them.
  if (kind === 'hold') {
    start[A] = 0;
    for (const d of L.dry) start[d] = 0;
  }
  const keep = kind === 'hold' ? { x: a.x - L.side * 700, y: a.y + 550 } : L.door;
  const on0 = seed % 4;
  const on1 = (on0 + 1 + (idiv(seed, 4) % 3)) % 4;
  let to = (on1 + 1) % 4;
  if (to === on0) to = (to + 1) % 4;
  const H = S.hold;
  return {
    kind,
    pools: L.pools.map((p) => ({ x: p.x, y: p.y })),
    half: SALT_HALF_X,
    halfY: SALT_HALF_Y,
    cell,
    start,
    cap: SALT_CAP,
    altar: A,
    house: L.house,
    door: L.door,
    marit,
    keep,
    ...(kind === 'fog' ? { fog: { speed: S.fog.speed, follow: S.fog.follow, done: S.fog.done } } : {}),
    ...(kind === 'sluice'
      ? {
          gates: L.gates.map((g) => ({ a: g.a, b: g.b, down: g.down, x: g.x, y: g.y })),
          sluice: { turn: S.sluice.turn, reach: S.sluice.reach, flow: S.sluice.flow, back: S.sluice.back, warn: S.sluice.warn, lose: S.sluice.lose, dry: S.sluice.dry, drowned: { first: S.sluice.drowned.first, every: S.sluice.drowned.every, back: S.sluice.drowned.back }, nuno: S.sluice.nuno, drownedHp },
        }
      : {}),
    ...(kind === 'hold'
      ? {
          ways: L.ways.map((w) => ({ pts: w.pts.map((p) => ({ x: p.x, y: p.y })), choke: w.choke })),
          hold: {
            every: H.every,
            first: H.first,
            throw: H.throw,
            radius: H.radius,
            last: H.last,
            wet: H.wet,
            salted: H.salted,
            slowBp: H.slowBp,
            band: { ...H.band },
            on: [on0, on1],
            swap: H.swap,
            to,
            old: { hp: oldHp, at: H.old.at, every: H.old.every, warn: H.old.warn, linger: H.old.linger, range: H.old.range, bp: H.old.bp, bleed: H.old.bleed, bleedEvery: H.old.bleedEvery, cover: H.old.cover, heroBp: H.old.heroBp },
          },
        }
      : {}),
  };
};

/** The flats as a fight starts (the renderer's scenes build one too). */
export const saltFresh = (r: SaltRules): SaltState => ({
  level: r.start.slice(),
  locked: (r.gates ?? []).map(() => 0),
  flows: [],
  job: -1,
  held: 0,
  back: r.sluice?.back ?? -1,
  returns: 0,
  dried: -1,
  turned: 0,
  drowned: r.sluice?.drowned.first ?? -1,
  nuno: false,
  swing: 0,
  nunoSwing: 0,
  ready: r.hold?.first ?? 0,
  throwing: null,
  circles: [],
  salted: [],
  saltedUntil: [],
  nextBand: r.hold?.band.first ?? -1,
  bands: 0,
  onWay: [],
  way: [],
  step: [],
  old: { id: 0, mode: 0, t: 0, x: 0, y: 0, next: 0 },
  bleed: 0,
  bleedNext: 0,
  leaps: 0,
  covered: 0,
  prints: false,
  heroHp: 0,
  trail: [],
});

/** Index of a sorted list's value (−1: not in it). */
const find = (list: readonly number[], v: number): number => {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = list[m] as number;
    if (x === v) return m;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** The pool under a world point by the board's cells (−1 a dyke, −2 off the board or the house). */
export const saltPoolOf = (s: BattleState, x: number, y: number): number => {
  const r = s.rules?.senda?.salt;
  if (!r) return -2;
  const c = cellAtWorld(s, x, y);
  return c < 0 ? -2 : (r.cell[c] ?? -2);
};

/** Whether ground is salt: a dyke, or a pool gone dry (not the altar's fresh mud in the test). */
export const saltGround = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  if (!r) return false;
  const p = saltPoolOf(s, x, y);
  if (p === -1) return true;
  if (p < 0) return false;
  const lv = w ? (w.level[p] as number) : (r.start[p] as number);
  return lv === 0 && !(r.kind === 'hold' && p === r.altar);
};

/** Whether a nightmare shows in the fog: on salt, in a circle of Marit's, or salted a while ago. */
export const saltSeen = (s: BattleState, e: Enemy): boolean => {
  // (The fog hides the stalkers; the drowned climbing out of the pools are seen, big and slow.)
  if (e.kind !== 'stalker') return true;
  const w = s.senda?.salt;
  if (w) {
    if (w.salted.length) {
      const i = find(w.salted, e.id);
      if (i >= 0 && (w.saltedUntil[i] as number) > s.tick) return true;
    }
    const R = s.rules?.senda?.salt?.hold?.radius ?? 0;
    for (const c of w.circles) if (withinRange(e.x, e.y, c.x, c.y, R)) return true;
  }
  return saltGround(s, e.x, e.y);
};

/** The hero sent to a gate (the gates), or Marit told where to throw (the test: `at` a cell). */
export const saltOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  if (!r || !w) return;
  if (r.kind === 'sluice' && r.gates && at >= 0 && at < r.gates.length) {
    w.job = at;
    w.held = 0;
    const g = r.gates[at] as NonNullable<SaltRules['gates']>[number];
    orderMove(s, h, g.x, g.y);
    h.target = 0;
    h.forced = false;
    return;
  }
  if (r.kind === 'hold' && r.hold) saltThrow(s, at);
};

/** Marit throws her salt at a cell (if she stands and it is ready). */
export const saltThrow = (s: BattleState, at: number): boolean => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  const m = npcOf(s, 'marit');
  if (!r?.hold || !w || !m || m.hp <= 0 || s.tick < w.ready || w.throwing) return false;
  const R = s.map.radius;
  if (at < 0 || at >= (2 * R + 1) * (2 * R + 1) || (r.cell[at] ?? -2) === -2) return false;
  const c = cellCenter(R, at);
  w.throwing = { x: c.x, y: c.y, at: s.tick + r.hold.throw };
  w.ready = s.tick + r.hold.every;
  ev(s, { t: 'senda', kind: 'throw', npc: m.id, x: c.x, y: c.y });
  return true;
};

/* ---------- the fog (act 1) ---------- */

const fogTick = (s: BattleState, r: SaltRules, w: SaltState, cue: (c: string) => void): void => {
  const F = r.fog;
  const m = npcOf(s, 'marit');
  if (!F || !m || m.hp <= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // She strikes what she sees by her; else she follows the hero (a blow on her stops her a moment).
  // (A blow doesn't stop her here: she keeps limping on. In the water, to the knee, at a quarter of her pace.)
  if (m.flinch > 0) m.flinch--;
  // Where the hero has walked (she goes the way it went, not straight at it).
  if (h) {
    const n = w.trail.length;
    const lx = n ? (w.trail[n - 2] as number) : m.x;
    const ly = n ? (w.trail[n - 1] as number) : m.y;
    if (!withinRange(h.x, h.y, lx, ly, 500)) {
      w.trail.push(h.x, h.y);
      if (w.trail.length > 64) w.trail.splice(0, 2);
    }
  }
  if (h && !withinRange(h.x, h.y, m.x, m.y, F.follow)) {
    // The oldest point of the trail still ahead of her (those she has reached dropped).
    while (w.trail.length > 2 && withinRange(m.x, m.y, w.trail[0] as number, w.trail[1] as number, 300)) w.trail.splice(0, 2);
    const tx = w.trail.length ? (w.trail[0] as number) : h.x;
    const ty = w.trail.length ? (w.trail[1] as number) : h.y;
    m.moving = true;
    stepToward(m, tx, ty, saltPoolOf(s, m.x, m.y) >= 0 ? F.speed >> 2 : F.speed);
    if (tx !== m.x || ty !== m.y) m.facing = iatan2(ty - m.y, tx - m.x);
  } else w.swing = guardTick(s, m, { x: m.x, y: m.y }, r.marit, w.swing, (e) => !saltSeen(s, e));
  if (!w.prints && s.tick % 6 === 0) {
    for (const e of s.enemies) {
      if (e.hp > 0 && saltPoolOf(s, e.x, e.y) === -1) {
        w.prints = true;
        cue('prints');
        break;
      }
    }
  }
  if (w.dried < 0 && withinRange(m.x, m.y, r.door.x, r.door.y, F.done)) {
    w.dried = s.tick;
    ev(s, { t: 'senda', kind: 'eye', npc: m.id, x: m.x, y: m.y });
    cue('house');
  }
};

/* ---------- the gates (act 2) ---------- */

const sluiceTick = (s: BattleState, r: SaltRules, w: SaltState, cue: (c: string) => void): void => {
  const G = r.gates;
  const U = r.sluice;
  if (!G || !U) return;
  // The hero's hands on the gate.
  const h = s.heroes.find((x) => x.downTicks === 0);
  // A blow on the hero at the wheel and the turn starts over.
  const struckNow = !!h && h.hp < w.heroHp;
  if (h) w.heroHp = h.hp;
  if (h && w.job >= 0) {
    const g = G[w.job] as (typeof G)[number];
    if (struckNow) w.held = 0;
    if (h.moving || !withinRange(h.x, h.y, g.x, g.y, U.reach)) {
      w.held = 0;
      if (!h.moving) w.job = -1;
    } else if (++w.held >= U.turn) {
      if ((w.locked[w.job] as number) === 1) ev(s, { t: 'senda', kind: 'stuck', npc: '', x: g.x, y: g.y, n: w.job });
      else {
        w.locked[w.job] = 1;
        w.flows.push({ g: w.job, end: s.tick + U.flow });
        w.turned++;
        ev(s, { t: 'senda', kind: 'gate', npc: '', x: g.x, y: g.y, n: w.job });
      }
      w.job = -1;
      w.held = 0;
    }
  }
  // The water that has run.
  while (w.flows.length && (w.flows[0] as { end: number }).end <= s.tick) {
    const f = w.flows.shift() as { g: number; end: number };
    const g = G[f.g] as (typeof G)[number];
    const was = [w.level[g.a] as number, w.level[g.b] as number];
    const [na, nb] = sluiceFlow(g.down, was[0] as number, was[1] as number, r.cap);
    w.level[g.a] = na;
    w.level[g.b] = nb;
    ev(s, { t: 'senda', kind: 'flowed', npc: '', x: g.x, y: g.y, n: f.g });
    for (const p of [g.a, g.b])
      if ((w.level[p] as number) === 0 && (p === g.a ? was[0] : was[1]) !== 0) {
        const c = r.pools[p] as { x: number; y: number };
        ev(s, { t: 'senda', kind: 'dry', npc: '', x: c.x, y: c.y, n: p });
        if (p !== r.altar) cue('dry');
      }
    if ((w.level[r.altar] as number) === 0 && w.dried < 0) {
      w.dried = s.tick;
      cue('eye');
    }
  }
  // Nuño comes out to help after a few gates.
  const nuno = npcOf(s, 'nuno');
  if (nuno && !w.nuno && w.turned >= U.nuno) {
    w.nuno = true;
    nuno.away = false;
    nuno.x = r.door.x + 500;
    nuno.y = r.door.y;
    ev(s, { t: 'senda', kind: 'nuno', npc: 'nuno', x: nuno.x, y: nuno.y });
    cue('nuno');
  }
  if (nuno && w.nuno && nuno.hp > 0) w.nunoSwing = guardTick(s, nuno, h ? { x: h.x, y: h.y } : r.door, r.marit, w.nunoSwing);
  const m = npcOf(s, 'marit');
  if (m) w.swing = guardTick(s, m, r.keep, r.marit, w.swing, (e) => !saltSeen(s, e));
  // The sea: seen coming, then in under the dykes.
  const T = s.waveT;
  if (w.dried < 0 && T === w.back - U.warn) ev(s, { t: 'senda', kind: 'sea', npc: '', x: 0, y: 0, n: 1 });
  if (w.dried < 0 && T >= w.back) {
    w.back = T + U.back;
    w.returns++;
    w.level = r.start.slice();
    w.locked = w.locked.map(() => 0);
    w.flows = [];
    ev(s, { t: 'senda', kind: 'sea', npc: '', x: 0, y: 0, n: 0 });
    cue(`back${w.returns}`);
    for (let k = 0; k < U.drowned.back; k++) drown(s, r, w, k);
  }
  if (w.dried < 0 && T >= w.drowned) {
    w.drowned = T + U.drowned.every;
    drown(s, r, w, 0);
  }
};

/** A drowned out of the full pool nearest the hero (not the one it stands in), the k-th of its band. */
const drown = (s: BattleState, r: SaltRules, w: SaltState, k: number): void => {
  const h = s.heroes[0];
  if (!h || !r.sluice) return;
  const under = saltPoolOf(s, h.x, h.y);
  let best = -1;
  let bd = 0x7fffffff;
  for (let p = 0; p < r.pools.length; p++) {
    if (p === under || (w.level[p] as number) < r.cap) continue;
    const c = r.pools[p] as { x: number; y: number };
    // (Inside the board: its middle on a cell.)
    if (saltPoolOf(s, c.x, c.y) !== p) continue;
    const d = Math.abs(c.x - h.x) + Math.abs(c.y - h.y);
    if (d < bd || (d === bd && p < best)) {
      bd = d;
      best = p;
    }
  }
  if (best < 0) return;
  const c = r.pools[best] as { x: number; y: number };
  const e = spawnEnemy(s, 'drowned', 1, false, 0, c.x + k * 400, c.y, r.sluice.drownedHp);
  e.anim = 'walk';
};

/* ---------- the test (act 3) ---------- */

const holdTick = (s: BattleState, r: SaltRules, w: SaltState, cue: (c: string) => void): void => {
  const H = r.hold;
  const ways = r.ways;
  if (!H || !ways) return;
  const m = npcOf(s, 'marit');
  // The salt lands; the circles melt; what is in one is seen, slowed and salted.
  if (w.throwing && s.tick >= w.throwing.at) {
    const t = w.throwing;
    w.throwing = null;
    const wet = !saltGround(s, t.x, t.y);
    w.circles.push({ x: t.x, y: t.y, until: s.tick + (wet ? H.wet : H.last), wet });
    ev(s, { t: 'senda', kind: 'salt', npc: '', x: t.x, y: t.y, n: wet ? 1 : 0 });
  }
  if (w.circles.length) {
    w.circles = w.circles.filter((c) => c.until > s.tick);
    for (const e of s.enemies) {
      if (e.hp <= 0) continue;
      for (const c of w.circles) {
        if (!withinRange(e.x, e.y, c.x, c.y, H.radius)) continue;
        e.slowBp = imax(e.slowBp, H.slowBp);
        e.slowTicks = imax(e.slowTicks, 2);
        salt(w, e.id, s.tick + H.salted);
        break;
      }
    }
  }
  if (w.salted.length && s.tick % 24 === 0) {
    for (let i = w.salted.length - 1; i >= 0; i--) {
      if ((w.saltedUntil[i] as number) > s.tick) continue;
      w.salted.splice(i, 1);
      w.saltedUntil.splice(i, 1);
    }
  }
  // The bands, along the ways in use.
  const T = s.waveT;
  if (T >= w.nextBand) {
    w.nextBand = T + H.band.every;
    const on = T >= H.swap ? [H.on[0], H.to] : H.on;
    // Each band by one of the two ways in turn; every third, by both.
    const both = w.bands % 3 === 2;
    const list = both ? on : [on[w.bands % 2] as number];
    w.bands++;
    let k = 0;
    for (const wi of list) {
      const way = ways[wi] as NonNullable<SaltRules['ways']>[number];
      const n = both ? imax(1, H.band.n - 1) : H.band.n;
      for (let i = 0; i < n; i++, k++) {
        const p = way.pts[0] as { x: number; y: number };
        const e = spawnEnemy(s, 'stalker', 1, false, 0, p.x + (i % 2) * 450 - 200, p.y + i * 380);
        if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
        w.onWay.push(e.id);
        w.way.push(wi);
        w.step.push(1);
      }
    }
  }
  // The old stalker comes out of the fog.
  const O = H.old;
  if (!w.old.id && T >= O.at) {
    const p = (ways[H.on[0]] as NonNullable<SaltRules['ways']>[number]).pts[0] as { x: number; y: number };
    const e = spawnEnemy(s, 'stalker', 1, true, 0, p.x, p.y, O.hp);
    w.old.id = e.id;
    w.old.next = s.tick + idiv(O.every, 2);
    cue('old');
  }
  // Marit's bleeding.
  if (m && m.hp > 0 && w.bleed > 0 && s.tick >= w.bleedNext) {
    w.bleed--;
    w.bleedNext = s.tick + O.bleedEvery;
    m.hp = imax(0, m.hp - 1);
    ev(s, { t: 'senda', kind: m.hp > 0 ? 'hit' : 'down', npc: m.id, x: m.x, y: m.y });
  }
  if (m) w.swing = guardTick(s, m, r.keep, r.marit, w.swing, (e) => !saltSeen(s, e));
};

const salt = (w: SaltState, id: number, until: number): void => {
  let i = 0;
  while (i < w.salted.length && (w.salted[i] as number) < id) i++;
  if (w.salted[i] === id) w.saltedUntil[i] = imax(w.saltedUntil[i] as number, until);
  else {
    w.salted.splice(i, 0, id);
    w.saltedUntil.splice(i, 0, until);
  }
};

/**
 * The old stalker's way: to Marit; crouched, her spot marked `warn`; the leap (onto the hero, if it stands there); on
 * her `linger` (its claws in: its blows count), then back into the fog toward its dry pool, and again.
 */
const oldMove = (s: BattleState, r: SaltRules, w: SaltState, e: Enemy, pace: number): boolean => {
  const O = r.hold?.old;
  const m = npcOf(s, 'marit');
  if (!O || !m || m.hp <= 0) return false;
  const o = w.old;
  if (o.mode === 1) {
    e.anim = 'cast';
    if (s.tick < o.t) return true;
    // The leap: it lands on the spot.
    e.x = o.x;
    e.y = o.y;
    o.mode = 2;
    o.t = s.tick + O.linger;
    o.next = s.tick + O.every;
    w.leaps++;
    const h = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, o.x, o.y, O.cover));
    if (h) {
      w.covered++;
      damageHero(s, h, mulBp(h.stats.maxHp, O.heroBp), e, 'physical', true);
      ev(s, { t: 'senda', kind: 'cover', npc: m.id, x: o.x, y: o.y });
    } else if (withinRange(m.x, m.y, o.x, o.y, O.cover)) {
      m.hp = imax(0, m.hp - imax(1, idiv(m.maxHp * O.bp + 9999, 10000)));
      w.bleed = O.bleed;
      w.bleedNext = s.tick + O.bleedEvery;
      ev(s, { t: 'senda', kind: m.hp > 0 ? 'hit' : 'down', npc: m.id, x: m.x, y: m.y });
    }
    ev(s, { t: 'senda', kind: 'leap', npc: '', x: o.x, y: o.y });
    return true;
  }
  if (o.mode === 2) {
    e.anim = 'attack';
    if (s.tick < o.t) return true;
    o.mode = 3;
    return true;
  }
  if (o.mode === 3) {
    // Back into the fog, to its way's dry pool, till it is time again.
    const way = r.ways?.[r.hold?.on[0] ?? 0];
    const p = way?.pts[1] ?? { x: m.x, y: m.y - 6000 };
    if (s.tick >= o.next - O.warn - sec(4) || withinRange(e.x, e.y, p.x, p.y, 400)) {
      o.mode = 0;
      return true;
    }
    e.anim = 'walk';
    stepToward(e, p.x, p.y, pace);
    return true;
  }
  if (s.tick >= o.next && withinRange(e.x, e.y, m.x, m.y, O.range)) {
    o.mode = 1;
    o.t = s.tick + O.warn;
    o.x = m.x;
    o.y = m.y;
    e.anim = 'cast';
    ev(s, { t: 'senda', kind: 'leapWarn', npc: m.id, x: o.x, y: o.y });
    sendaCue(s, 'leap');
    return true;
  }
  if (!withinRange(e.x, e.y, m.x, m.y, imin(O.range, 2600))) {
    e.anim = 'walk';
    stepToward(e, m.x, m.y, pace);
    return true;
  }
  // Waiting off her, out of reach, for its next leap.
  e.anim = 'idle';
  return true;
};

/**
 * The flats' nightmares (from the enemies' tick, before their own powers): the stalkers along their way to the altar,
 * the old stalker by its rule. False: the usual hunt.
 */
export const saltHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  if (!r || !w || e.stun > 0 || r.kind !== 'hold' || !r.ways) return false;
  if (e.id === w.old.id) return oldMove(s, r, w, e, pace(e));
  const i = find(w.onWay, e.id);
  if (i < 0) return false;
  const way = r.ways[w.way[i] as number] as NonNullable<SaltRules['ways']>[number];
  const k = w.step[i] as number;
  const p = way.pts[k];
  if (!p) {
    w.onWay.splice(i, 1);
    w.way.splice(i, 1);
    w.step.splice(i, 1);
    return false;
  }
  if (withinRange(e.x, e.y, p.x, p.y, 320)) {
    w.step[i] = k + 1;
    return true;
  }
  e.anim = 'walk';
  stepToward(e, p.x, p.y, pace(e));
  return true;
};

export const saltTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.salt;
  const st = s.senda;
  if (!r || !st) return;
  if (!st.salt) {
    st.salt = saltFresh(r);
    // Nuño waits inside the gate house until he comes out to help.
    const nuno = npcOf(s, 'nuno');
    if (nuno) nuno.away = true;
  }
  const w = st.salt;
  if (!s.started) return;
  if (r.kind === 'fog') fogTick(s, r, w, cue);
  else if (r.kind === 'sluice') sluiceTick(s, r, w, cue);
  else holdTick(s, r, w, cue);
  // The dead off the way list now and then.
  if (w.onWay.length && s.tick % 36 === 0) {
    for (let i = w.onWay.length - 1; i >= 0; i--) {
      const id = w.onWay[i] as number;
      if (s.enemies.some((e) => e.id === id && e.hp > 0)) continue;
      w.onWay.splice(i, 1);
      w.way.splice(i, 1);
      w.step.splice(i, 1);
    }
  }
};

/** The fog: Marit's way to the door, in hexes. The gates: the altar's pool's marks out. */
export const saltGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.kind === 'fog') {
    const m = npcOf(s, 'marit');
    const need = 12;
    if (!m) return { done: 0, need };
    const d = idiv(Math.abs(m.x - r.door.x) + Math.abs(m.y - r.door.y), HEX);
    return { done: w.dried >= 0 ? need : imax(0, need - imin(need, d)), need };
  }
  return { done: r.cap - imin(r.cap, w.level[r.altar] as number), need: r.cap };
};

/** The fog won with Marit at the door; the gates with the altar dry (`dry` after), lost with the returns spent. */
export const saltResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  if (!r || !w) return null;
  if (r.kind === 'fog') return w.dried >= 0 ? 'won' : null;
  if (r.kind === 'sluice' && r.sluice) {
    if (w.dried >= 0 && s.tick >= w.dried + r.sluice.dry) return 'won';
    if (w.returns >= r.sluice.lose) return 'lost';
  }
  return null;
};
